import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    payments: [],
    loadedPayments: false,
    quantity: 50,
    selectedPayment: {},
    isLoading: false,
    paymentsForFilter: [],
    paymentsForDonor: [],
    paymentsForPledge: [],
    paymentsForParent: undefined,
    pendingPayment: null,
    recurringPayments: [],
    loadedRecurringPayments: false,
    recurringQuantity: 50,
    selectedRecurringPayment: {},
    recurringIsLoading: false,
    recurringPaymentsForFilter: [],
    recurringPaymentsForDonor: [],
    recurringPaymentsForParent: undefined,
    // Invalidate-on-mutation: list refetches when true (separate for school vs regular)
    paymentsListInvalidated: false,
    schoolPaymentsListInvalidated: false,
    // School-specific state
    schoolPayments: [],
    loadedSchoolPayments: false,
    schoolQuantity: 50,
    selectedSchoolPayment: {},
    schoolIsLoading: false,
    schoolRecurringPayments: [],
    loadedSchoolRecurringPayments: false,
    schoolRecurringQuantity: 50,
    selectedSchoolRecurringPayment: {},
    schoolRecurringIsLoading: false,
}

const paymentsSlice = createSlice({
    name: "payments",
    initialState,
    reducers: {
        loadPayments(state, action) {
            state.payments = action.payload.payments;
            state.quantity = action.payload.payments.length
            state.loadedPayments = true;
        },
        showLessRows(state){
            state.payments = state.payments.slice(0, state.quantity - 51)
            state.quantity = state.quantity - 50
        },
        setIsLoading(state, action) {
            state.isLoading = action.payload
        },
        setSelectedPayment(state, action) {
            if (action.payload) {
                const id = action.payload
                const selectedPayment = state.payments.find(payment => payment.PaymentsID === id)
                if (selectedPayment) {
                    state.selectedPayment = selectedPayment
                }
                else if(typeof action.payload === 'object'){
                    state.selectedPayment = action.payload
                }
            }
        },
        resetSelectedPayment(state) {
            state.selectedPayment = {}
        },
        loadPaymentsForDonor(state, action){
            state.paymentsForDonor = action.payload.payments
        },
        resetPaymentsForDonor(state){
            state.paymentsForDonor = []
        },
        loadPaymentsForPledge(state, action){
            state.paymentsForPledge = action.payload.payments
        },
        resetPaymentsForPledge(state){
            state.paymentsForPledge = []
        },
        loadPaymentsForParent(state, action){
            // Always set to an array, even if empty (to distinguish from undefined = not fetched)
            state.paymentsForParent = action.payload.payments || []
        },
        resetPaymentsForParent(state){
            state.paymentsForParent = undefined
        },
        setPendingPayment(state, action){
            state.pendingPayment = action.payload
        },
        resetPendingPayment(state){
            state.pendingPayment = null
        },
        //recurring payments
        loadRecurringPayments(state, action) {
            state.recurringPayments = action.payload.payments;
            state.loadedRecurringPayments = true;
            state.recurringQuantity = action.payload.payments.length
        },
        showMoreRecurringRows(state) {
            state.recurringQuantity = state.recurringQuantity + 50
        },
        showLessRecurringRows(state){
            state.recurringPayments = state.recurringPayments.slice(0, state.recurringQuantity - 51)
            state.recurringQuantity = state.recurringQuantity - 50
        },
        setRecurringIsLoading(state, action) {
            state.recurringIsLoading = action.payload
        },
        setSelectedRecurringPayment(state, action) {
            if (action.payload) {
                const id = action.payload
                const selectedPayment = state.recurringPayments.find(payment => payment.PledgeRecurringID === id)
                if (selectedPayment) {
                    state.selectedRecurringPayment = selectedPayment
                }
                else if(typeof action.payload === 'object'){
                    state.selectedRecurringPayment = action.payload
                }
            }
        },

        resetSelectedRecPayment(state) {
            state.selectedRecurringPayment = {}
        },
        loadRecurringPaymentsForDonor(state, action){
            state.recurringPaymentsForDonor = action.payload.payments
        },
        resetRecurringPaymentsForDonor(state){
            state.recurringPaymentsForDonor = []
        },
        loadRecurringPaymentsForParent(state, action){
            // Always set to an array, even if empty (to distinguish from undefined = not fetched)
            state.recurringPaymentsForParent = action.payload.payments || []
        },
        resetRecurringPaymentsForParent(state){
            state.recurringPaymentsForParent = undefined
        },
        removeDeletedPayment(state, action){
            state.payments = state.payments.filter(p => p.PaymentsID !== action.payload)
            if(state.payments.length > 0){
                state.payments[0].TotalPayments -= 1
            }
        },
        updatePaymentInLists(state, action) {
            // Update payment in all lists where it exists
            const { paymentId, updates } = action.payload;
            
            // Update in main payments list
            const paymentIndex = state.payments.findIndex(p => p.PaymentsID === paymentId);
            if (paymentIndex !== -1) {
                state.payments[paymentIndex] = { ...state.payments[paymentIndex], ...updates };
            }
            
            // Update in school payments list
            const schoolPaymentIndex = state.schoolPayments.findIndex(p => p.PaymentsID === paymentId);
            if (schoolPaymentIndex !== -1) {
                state.schoolPayments[schoolPaymentIndex] = { ...state.schoolPayments[schoolPaymentIndex], ...updates };
            }
            
            // Update in paymentsForDonor list
            const donorPaymentIndex = state.paymentsForDonor.findIndex(p => p.PaymentsID === paymentId);
            if (donorPaymentIndex !== -1) {
                state.paymentsForDonor[donorPaymentIndex] = { ...state.paymentsForDonor[donorPaymentIndex], ...updates };
            }
            
            // Update in paymentsForParent list
            if (state.paymentsForParent && Array.isArray(state.paymentsForParent)) {
                const parentPaymentIndex = state.paymentsForParent.findIndex(p => p.PaymentsID === paymentId);
                if (parentPaymentIndex !== -1) {
                    state.paymentsForParent[parentPaymentIndex] = { ...state.paymentsForParent[parentPaymentIndex], ...updates };
                }
            }
            
            // Update in paymentsForPledge list
            const pledgePaymentIndex = state.paymentsForPledge.findIndex(p => p.PaymentsID === paymentId);
            if (pledgePaymentIndex !== -1) {
                state.paymentsForPledge[pledgePaymentIndex] = { ...state.paymentsForPledge[pledgePaymentIndex], ...updates };
            }
        },
        removeDeletedRecPayment(state, action){
            state.recurringPayments = state.recurringPayments.filter(p => p.PledgeRecurringID !== action.payload)
            if(state.recurringPayments.length > 0){
                state.recurringPayments[0].TotalRecurring -= 1
            }
        },
        updateRecurringPaymentInLists(state, action) {
            // Update recurring payment in all lists where it exists
            const { paymentId, updates } = action.payload;
            
            // Update in main recurring payments list
            const paymentIndex = state.recurringPayments.findIndex(p => p.PledgeRecurringID === paymentId);
            if (paymentIndex !== -1) {
                state.recurringPayments[paymentIndex] = { ...state.recurringPayments[paymentIndex], ...updates };
            }
            
            // Update in school recurring payments list
            const schoolPaymentIndex = state.schoolRecurringPayments.findIndex(p => p.PledgeRecurringID === paymentId);
            if (schoolPaymentIndex !== -1) {
                state.schoolRecurringPayments[schoolPaymentIndex] = { ...state.schoolRecurringPayments[schoolPaymentIndex], ...updates };
            }
            
            // Update in recurringPaymentsForDonor list
            const donorPaymentIndex = state.recurringPaymentsForDonor.findIndex(p => p.PledgeRecurringID === paymentId);
            if (donorPaymentIndex !== -1) {
                state.recurringPaymentsForDonor[donorPaymentIndex] = { ...state.recurringPaymentsForDonor[donorPaymentIndex], ...updates };
            }
            
            // Update in recurringPaymentsForParent list
            if (state.recurringPaymentsForParent && Array.isArray(state.recurringPaymentsForParent)) {
                const parentPaymentIndex = state.recurringPaymentsForParent.findIndex(p => p.PledgeRecurringID === paymentId);
                if (parentPaymentIndex !== -1) {
                    state.recurringPaymentsForParent[parentPaymentIndex] = { ...state.recurringPaymentsForParent[parentPaymentIndex], ...updates };
                }
            }
        },
        // Invalidate list so PaymentsForm refetches (after add payment or apply/clear filter)
        invalidatePaymentsList(state, action) {
            const isSchool = action.payload === true;
            if (isSchool) state.schoolPaymentsListInvalidated = true;
            else state.paymentsListInvalidated = true;
        },
        clearPaymentsListInvalidated(state, action) {
            const isSchool = action.payload === true;
            if (isSchool) state.schoolPaymentsListInvalidated = false;
            else state.paymentsListInvalidated = false;
        },
        // School payments reducers
        loadSchoolPayments(state, action) {
            state.schoolPayments = action.payload.payments;
            state.schoolQuantity = action.payload.payments.length
            state.loadedSchoolPayments = true;
        },
        setSchoolIsLoading(state, action) {
            state.schoolIsLoading = action.payload
        },
        setSelectedSchoolPayment(state, action) {
            if (action.payload) {
                const id = action.payload
                const selectedPayment = state.schoolPayments.find(payment => payment.PaymentsID === id)
                if (selectedPayment) {
                    state.selectedSchoolPayment = selectedPayment
                }
                else if(typeof action.payload === 'object'){
                    state.selectedSchoolPayment = action.payload
                }
            }
        },
        resetSelectedSchoolPayment(state) {
            state.selectedSchoolPayment = {}
        },
        // School recurring payments reducers
        loadSchoolRecurringPayments(state, action) {
            state.schoolRecurringPayments = action.payload.payments;
            state.loadedSchoolRecurringPayments = true;
            state.schoolRecurringQuantity = action.payload.payments.length
        },
        setSchoolRecurringIsLoading(state, action) {
            state.schoolRecurringIsLoading = action.payload
        },
        setSelectedSchoolRecurringPayment(state, action) {
            if (action.payload) {
                const id = action.payload
                const selectedPayment = state.schoolRecurringPayments.find(payment => payment.PledgeRecurringID === id)
                if (selectedPayment) {
                    state.selectedSchoolRecurringPayment = selectedPayment
                }
                else if(typeof action.payload === 'object'){
                    state.selectedSchoolRecurringPayment = action.payload
                }
            }
        },
        resetSelectedSchoolRecPayment(state) {
            state.selectedSchoolRecurringPayment = {}
        },
        removeDeletedSchoolPayment(state, action){
            state.schoolPayments = state.schoolPayments.filter(p => p.PaymentsID !== action.payload)
            if(state.schoolPayments.length > 0){
                state.schoolPayments[0].TotalPayments -= 1
            }
        },
        removeDeletedSchoolRecPayment(state, action){
            state.schoolRecurringPayments = state.schoolRecurringPayments.filter(p => p.PledgeRecurringID !== action.payload)
            if(state.schoolRecurringPayments.length > 0){
                state.schoolRecurringPayments[0].TotalRecurring -= 1
            }
        },
        resetState:() => initialState,
    },
});

export const paymentsActions = paymentsSlice.actions;

export default paymentsSlice;
